import { useState, useEffect, useCallback } from 'react'
import { Card, CardBody, CardHeader, Spinner, Button as HeroButton } from '@heroui/react'
import { Badge } from '@trafiflow/shared-ui'
import { useNavigate } from 'react-router-dom'
import { changelogApi, type ChangelogEntry } from '../../lib/funnel-api'
import { formatChanges, formatRelativeTime } from '../../lib/format-changes'

function getActionVariant(action: string) {
  switch (action) {
    case 'created': return 'success' as const
    case 'deleted': return 'critical' as const
    default: return 'default' as const
  }
}

const PAGE_SIZE = 10

export function RecentChanges() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const navigate = useNavigate()

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true)
      const response = await changelogApi.list()
      const sorted = (response.data ?? [])
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setEntries(sorted)
    } catch {
      // Silently fail — dashboard section is non-critical
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  const visibleEntries = showAll ? entries : entries.slice(0, PAGE_SIZE)

  if (loading) {
    return (
      <Card>
        <CardBody className="flex items-center justify-center py-8">
          <Spinner size="sm" color="primary" />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between px-6 pt-5 pb-0">
        <h2 className="text-lg font-semibold text-foreground">Recent Changes</h2>
        <HeroButton
          variant="light"
          color="primary"
          size="sm"
          onPress={() => navigate('/funnels/changelog')}
        >
          View All
        </HeroButton>
      </CardHeader>
      <CardBody className="px-6 pb-5">
        {entries.length === 0 ? (
          <p className="text-sm text-default-500 text-center py-4">No recent changes</p>
        ) : (
          <>
            <div className="space-y-3">
              {visibleEntries.map((entry) => {
                const changeLines = formatChanges(entry.changes)
                return (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-default-200"
                  >
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <Badge variant={getActionVariant(entry.action)}>
                        {entry.action}
                      </Badge>
                      <span className="text-xs text-default-400 capitalize">{entry.entityType}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">{entry.actor}</span>
                        <span className="text-xs text-default-400">{formatRelativeTime(entry.createdAt)}</span>
                      </div>
                      <ul className="text-xs text-default-600 space-y-0.5">
                        {changeLines.slice(0, 3).map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                        {changeLines.length > 3 && (
                          <li className="text-default-400">+{changeLines.length - 3} more changes</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )
              })}
            </div>
            {entries.length > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-default-200">
                <span className="text-xs text-default-500">
                  Showing {visibleEntries.length} of {entries.length} entries
                </span>
                <HeroButton
                  variant="flat"
                  size="sm"
                  onPress={() => setShowAll(!showAll)}
                >
                  {showAll ? 'Show Less' : 'Load More'}
                </HeroButton>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}
